import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@mono/ui-components';
import { UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAddMembers } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { sharingApi } from '../../lib/api/sharingApi';
/** Maximum number of members allowed in a project */ const MAX_MEMBERS = 25;
export function AddMemberDialog({ open, onOpenChange, project }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const addMembers = useAddMembers();
    const currentMemberCount = project.members?.length || 0;
    const remainingSlots = MAX_MEMBERS - currentMemberCount;
    const existingMemberIds = useMemo(()=>new Set(project.members?.map((m)=>m.userId) || []), [
        project.members
    ]);
    // Reset state when dialog opens
    useEffect(()=>{
        if (open) {
            setSearchQuery('');
            setSearchResults([]);
            setSelectedUsers([]);
        }
    }, [
        open
    ]);
    // Search users with debounce
    useEffect(()=>{
        if (!searchQuery.trim()) {
            setSearchResults([]);
            return;
        }
        const timer = setTimeout(async ()=>{
            setIsSearching(true);
            try {
                const results = await sharingApi.searchUsers(searchQuery);
                // Filter out existing members
                const filtered = results.filter((user)=>!existingMemberIds.has(user.userId));
                setSearchResults(filtered);
            } catch  {
                // Don't expose raw error messages - use generic message for search failures
                toast.error('Failed to search users');
                setSearchResults([]);
            } finally{
                setIsSearching(false);
            }
        }, 300);
        return ()=>{
            clearTimeout(timer);
        };
    }, [
        searchQuery,
        existingMemberIds
    ]);
    const handleAddUser = (user, role)=>{
        // Check if already selected
        if (selectedUsers.some((u)=>u.userId === user.userId)) {
            toast.error(`${user.name} is already selected`);
            return;
        }
        // Check capacity
        if (selectedUsers.length >= remainingSlots) {
            toast.error(`Cannot add more than ${remainingSlots} members (${MAX_MEMBERS} member limit)`);
            return;
        }
        setSelectedUsers([
            ...selectedUsers,
            {
                ...user,
                role
            }
        ]);
        setSearchQuery('');
        setSearchResults([]);
    };
    const handleRemoveUser = (userId)=>{
        setSelectedUsers(selectedUsers.filter((u)=>u.userId !== userId));
    };
    const handleChangeRole = (userId, role)=>{
        setSelectedUsers(selectedUsers.map((u)=>u.userId === userId ? {
                ...u,
                role
            } : u));
    };
    const handleSubmit = async (e)=>{
        e.preventDefault();
        if (selectedUsers.length === 0) {
            toast.error('Please select at least one user to add');
            return;
        }
        try {
            const response = await addMembers.mutateAsync({
                projectId: project.projectId,
                members: selectedUsers.map((u)=>({
                        userId: u.userId,
                        email: u.email,
                        name: u.name,
                        role: u.role
                    }))
            });
            const addedCount = response.added.length;
            const skippedCount = response.skipped.length;
            if (addedCount > 0) {
                toast.success(`${addedCount} member${addedCount > 1 ? 's' : ''} added successfully`);
            }
            if (skippedCount > 0) {
                toast.info(`${skippedCount} user${skippedCount > 1 ? 's were' : ' was'} already a member`);
            }
            onOpenChange(false);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to add members'));
        }
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "sm:max-w-[600px]",
            "data-testid": "add-member-dialog",
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Add Members"
                        }),
                        /*#__PURE__*/ _jsxs(DialogDescription, {
                            children: [
                                "Search for users by name or email. ",
                                remainingSlots,
                                " of ",
                                MAX_MEMBERS,
                                " member slots available."
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit,
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    htmlFor: "user-search",
                                    className: "text-sm font-medium",
                                    children: "Search Users"
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    id: "user-search",
                                    type: "text",
                                    value: searchQuery,
                                    onChange: (e)=>setSearchQuery(e.target.value),
                                    placeholder: "Enter name or email...",
                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                    disabled: addMembers.isPending,
                                    maxLength: 100,
                                    "data-testid": "user-search-input"
                                }),
                                searchQuery && /*#__PURE__*/ _jsxs("div", {
                                    className: "border border-gray-200 rounded-md max-h-48 overflow-y-auto",
                                    children: [
                                        isSearching && /*#__PURE__*/ _jsx("div", {
                                            className: "p-3 text-sm text-gray-500",
                                            children: "Searching..."
                                        }),
                                        !isSearching && searchResults.length === 0 && /*#__PURE__*/ _jsxs("div", {
                                            className: "p-3 text-sm text-gray-500",
                                            children: [
                                                'No users found matching "',
                                                searchQuery,
                                                '"'
                                            ]
                                        }),
                                        !isSearching && searchResults.map((user)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between p-3 hover:bg-gray-50 border-b last:border-b-0",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-sm font-medium text-gray-900",
                                                                children: user.name
                                                            }),
                                                            /*#__PURE__*/ _jsx("p", {
                                                                className: "text-xs text-gray-500",
                                                                children: user.email
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex gap-2",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("button", {
                                                                type: "button",
                                                                onClick: ()=>handleAddUser(user, 'member'),
                                                                className: "px-3 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600",
                                                                "data-testid": `add-as-member-${user.userId}`,
                                                                children: "Add as Member"
                                                            }),
                                                            /*#__PURE__*/ _jsx("button", {
                                                                type: "button",
                                                                onClick: ()=>handleAddUser(user, 'view_only'),
                                                                className: "px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600",
                                                                "data-testid": `add-as-view-only-${user.userId}`,
                                                                children: "Add as View Only"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }, user.userId))
                                    ]
                                })
                            ]
                        }),
                        selectedUsers.length > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "text-sm font-medium",
                                    children: [
                                        "Selected Users (",
                                        selectedUsers.length,
                                        "/",
                                        remainingSlots,
                                        ")"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "border border-gray-200 rounded-md divide-y max-h-48 overflow-y-auto",
                                    children: selectedUsers.map((user)=>/*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between p-3",
                                            "data-testid": `selected-user-${user.userId}`,
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-sm font-medium text-gray-900",
                                                            children: user.name
                                                        }),
                                                        /*#__PURE__*/ _jsx("p", {
                                                            className: "text-xs text-gray-500",
                                                            children: user.email
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex items-center gap-2",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("select", {
                                                            value: user.role,
                                                            onChange: (e)=>handleChangeRole(user.userId, e.target.value),
                                                            className: "text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                                            "data-testid": `role-select-${user.userId}`,
                                                            children: [
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "member",
                                                                    children: "Member"
                                                                }),
                                                                /*#__PURE__*/ _jsx("option", {
                                                                    value: "view_only",
                                                                    children: "View Only"
                                                                })
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsx("button", {
                                                            type: "button",
                                                            onClick: ()=>handleRemoveUser(user.userId),
                                                            className: "p-1 text-gray-400 hover:text-red-600 rounded",
                                                            "aria-label": `Remove ${user.name}`,
                                                            "data-testid": `remove-selected-${user.userId}`,
                                                            children: /*#__PURE__*/ _jsx(X, {
                                                                className: "w-4 h-4"
                                                            })
                                                        })
                                                    ]
                                                })
                                            ]
                                        }, user.userId))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DialogFooter, {
                            className: "gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "outline",
                                    onClick: ()=>onOpenChange(false),
                                    disabled: addMembers.isPending,
                                    "aria-label": "Cancel adding members",
                                    "data-testid": "add-member-cancel-button",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsxs(Button, {
                                    type: "submit",
                                    isLoading: addMembers.isPending,
                                    disabled: selectedUsers.length === 0,
                                    "aria-label": "Add selected members",
                                    "data-testid": "add-member-submit-button",
                                    children: [
                                        /*#__PURE__*/ _jsx(UserPlus, {
                                            className: "w-4 h-4 mr-2"
                                        }),
                                        "Add ",
                                        selectedUsers.length > 0 ? `${selectedUsers.length} ` : '',
                                        "Member",
                                        selectedUsers.length !== 1 ? 's' : ''
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
